import { getUrl } from 'aws-amplify/storage'
import { logEvent } from 'app/logging/firebase'
import { withRetry } from 'app/api/client/retry'
import { waitForOnline } from 'app/services/networkStatus'
import {
    S3Client,
    CreateMultipartUploadCommand,
    UploadPartCommand,
    CompleteMultipartUploadCommand,
    AbortMultipartUploadCommand,
} from '@aws-sdk/client-s3'
import awsconfig from '../../../aws-exports'
import { audioRepository } from 'app/di/RepositoryContainer'
import { getAwsCredentials, getTuhiUserId } from 'app/store/slices/authSlice'
import {
    MultipartAudioDto,
    MultipartAudioStatus,
} from 'app/models/domain/consult/audio'

/**
 * Retry tuning for S3 multipart uploads on mobile networks (ACA-79).
 *
 * Cellular connections (5G handoffs, lift/basement dead spots, brief drops)
 * make individual multipart parts fail transiently. A long consult has many
 * 5MB parts, so the odds of at least one transient failure compound. Retry each
 * S3 operation persistently with exponential backoff + jitter (capped) so a
 * short blip doesn't fail the whole consult upload. waitForOnline() already
 * gates each attempt on connectivity.
 */
export const S3_CHUNK_RETRY = {
    maxAttempts: 8,
    initialDelay: 1000,
    maxDelay: 15000,
    backoffMultiplier: 2,
    jitter: true,
} as const

/** Retry for the one-shot start/complete multipart operations. */
export const S3_MULTIPART_RETRY = {
    maxAttempts: 6,
    initialDelay: 2000,
    maxDelay: 20000,
    backoffMultiplier: 2,
    jitter: true,
} as const

export interface StorageRepository {
    /**
     * The audioId of the current (or last aborted) multipart upload, or `null`
     * before one has started and after one completes.
     */
    readonly audioId: string | null
    /** Register a progress callback keyed by consultId (0..1). */
    setProgressCallback(
        callback: (consultId: string, progress: number) => void
    ): void
    /** Provide the total byte count so progress can be reported exactly. */
    setExpectedTotalBytes(totalBytes: number): void
    /** Progress is reported through setProgressCallback. */
    uploadAudio(lastChunk: Blob): Promise<{ storageKey: string; url: URL }>
    retryUploadAudio(
        fullBlob: Blob,
        audioId?: string
    ): Promise<{ storageKey: string; url: URL }>
    startMultipartUpload(): Promise<string>
    uploadChunk(chunk: Blob): Promise<void>
    abortMultipartUpload(): Promise<void>
    /**
     * Uploads a pre-recorded audio file (any format) as a consult's audio input
     * and returns the created audioId. The consultId is set on the AudioDto
     * BEFORE the S3 object lands so the backend trigger processes it directly.
     * `onProgress` (0..1) reports byte-based upload progress.
     */
    uploadAudioFile(
        file: File,
        consultId: string,
        onProgress?: (progress: number) => void
    ): Promise<{ audioId: string; storageKey: string; url: URL }>
}

enum StorageType {
    Audio = 'Audio',
    Attachment = 'Attachments',
}

enum Constants {
    Protected = 'protected',
    Separator = '/',
    Input = 'Input',
    Output = 'Output',
    Consults = 'Consults',
    WavExtension = '.wav',
    WebmExtension = '.webm',
}

export class S3StorageRepository implements StorageRepository {
    audioId: string | null = null

    private status: MultipartAudioStatus = 'idle'
    private activeUpload: MultipartAudioDto | null = null
    private uploadQueue: Promise<void> = Promise.resolve()
    private progressCallback:
        | ((consultId: string, progress: number) => void)
        | null = null
    private expectedTotalBytes: number | null = null

    setProgressCallback(
        callback: (consultId: string, progress: number) => void
    ): void {
        this.progressCallback = callback
    }

    setExpectedTotalBytes(totalBytes: number): void {
        this.expectedTotalBytes = totalBytes
    }

    get s3Client(): S3Client {
        return new S3Client({
            region: awsconfig.aws_project_region,
            credentials: async () => {
                const creds = getAwsCredentials()
                if (!creds) {
                    // Reading the keys off null used to throw a TypeError here;
                    // throw at the same point with a clear message instead.
                    throw new Error(
                        'No AWS credentials (auth.awsCredentials is not set)'
                    )
                }

                return {
                    accessKeyId: creds.accessKeyId,
                    secretAccessKey: creds.secretAccessKey,
                    sessionToken: creds.sessionToken,
                }
            },
        })
    }

    async startMultipartUpload(
        extension: string = Constants.WebmExtension,
        contentType: string = 'audio/webm',
        existingAudioId?: string
    ): Promise<string> {
        await this.abortMultipartUpload()

        const userId = getTuhiUserId()
        if (!userId) {
            // Recording is only reachable signed in. Without a user the upload
            // would go to `protected/undefined/...`, which the signed-in
            // identity cannot write, so stop before creating an AudioDto.
            throw new Error('No signed-in user (auth.currentUser is not set)')
        }
        // ACA-240: a retry re-uses the consult's existing AudioDto. The backend
        // trigger looks the AudioDto up by the uploaded file's name, so a new
        // id here (consultId null) is skipped and the consult never gets a note.
        const audioId = existingAudioId || (await audioRepository.create()).id
        this.audioId = audioId

        const fileName = audioId + extension
        const key = this.makeStoragePath(fileName, StorageType.Audio)
        const bucket = awsconfig.aws_user_files_s3_bucket

        try {
            const command = new CreateMultipartUploadCommand({
                Bucket: bucket,
                Key: key,
                ContentType: contentType,
                Metadata: {
                    userId: userId,
                    audioId: audioId,
                },
            })

            const response = await withRetry(async () => {
                await waitForOnline()
                return this.s3Client.send(command)
            }, S3_MULTIPART_RETRY)

            const uploadId = response.UploadId
            if (!uploadId) {
                // S3 always returns an UploadId on success; without one every
                // part upload would fail, so fail the start instead.
                throw new Error('S3 CreateMultipartUpload returned no UploadId')
            }

            this.activeUpload = MultipartAudioDto.empty(bucket, key, uploadId)

            logEvent(
                'INFO',
                's3_multipart_started',
                'S3 multipart upload started',
                { audioId: this.audioId }
            )

            this.progressCallback?.(audioId, 0.0)

            return uploadId
        } catch (error) {
            logEvent(
                'ERROR',
                's3_multipart_start_failed',
                'Failed to start S3 multipart upload',
                {
                    audioId: this.audioId,
                    error: error,
                }
            )
            throw error
        }
    }

    async uploadChunk(chunk: Blob): Promise<void> {
        if (this.status === 'aborting' || this.status === 'completing') {
            logEvent(
                'WARN',
                's3_chunk_skipped',
                'Chunk skipped due to upload state',
                {
                    audioId: this.audioId,
                    status: this.status,
                }
            )
            return
        }

        this.uploadQueue = this.uploadQueue.then(async () => {
            if (this.status === 'aborting') {
                logEvent(
                    'WARN',
                    's3_chunk_skipped',
                    'Chunk skipped due to upload state',
                    {
                        audioId: this.audioId,
                        status: this.status,
                    }
                )
                return
            }

            return this._doUploadChunk(chunk)
        })

        return this.uploadQueue
    }

    async uploadAudio(
        lastChunk: Blob
    ): Promise<{ storageKey: string; url: URL }> {
        this.status = 'completing'

        await this.uploadQueue
        await this._doUploadChunk(lastChunk)

        // Listeners key progress by their own consultId and ignore this id,
        // so a missing audioId (no upload started) still reports completion.
        const audioId = this.audioId ?? ''
        const result = await this.completeMultipartUpload()

        this.progressCallback?.(audioId, 1.0)

        return requireUploadResult(result)
    }

    async retryUploadAudio(
        fullBlob: Blob,
        audioId?: string
    ): Promise<{ storageKey: string; url: URL }> {
        const PART_SIZE = 5 * 1024 * 1024

        await this.startMultipartUpload(
            Constants.WebmExtension,
            'audio/webm',
            audioId
        )

        this.status = 'completing'

        let offset = 0
        while (offset < fullBlob.size) {
            const end = Math.min(offset + PART_SIZE, fullBlob.size)
            const chunk = fullBlob.slice(offset, end)
            await this._doUploadChunk(chunk)
            offset = end
        }

        return requireUploadResult(await this.completeMultipartUpload())
    }

    async uploadAudioFile(
        file: File,
        consultId: string,
        onProgress?: (progress: number) => void
    ): Promise<{ audioId: string; storageKey: string; url: URL }> {
        const PART_SIZE = 5 * 1024 * 1024
        // The S3 audio-upload trigger (tuhiOnConsultAudioUploaded) only fires on
        // `.webm`/`.caf` keys, so store every uploaded file under a `.webm` key
        // regardless of its real format. The backend converter uses ffmpeg,
        // which detects the true container/codec from the file content (not the
        // extension), so mp3/m4a/wav/etc. still convert correctly.
        const extension = Constants.WebmExtension
        const contentType = file.type || 'application/octet-stream'

        // Report byte-based progress via the per-chunk callback in _doUploadChunk.
        if (onProgress) {
            this.setProgressCallback((_audioId, progress) =>
                onProgress(progress)
            )
        }

        try {
            // Creates the AudioDto and starts the S3 multipart upload at
            // .../Consults/Audio/Input/{audioId}{extension}
            await this.startMultipartUpload(extension, contentType)
            // startMultipartUpload has just set it (or thrown).
            const audioId = this.audioId as string

            // Total size is known up front, so progress is exact bytes-uploaded / total.
            this.setExpectedTotalBytes(file.size)

            // Set consultId on the AudioDto BEFORE completing the upload. For a
            // multipart upload the S3 ObjectCreated event (which triggers the
            // transcription pipeline) fires on CompleteMultipartUpload, so the
            // AudioDto must already carry consultId to avoid the trigger race.
            await audioRepository.update(audioId, consultId, '', '')

            this.status = 'completing'

            let offset = 0
            while (offset < file.size) {
                const end = Math.min(offset + PART_SIZE, file.size)
                await this._doUploadChunk(file.slice(offset, end))
                offset = end
            }

            const { storageKey, url } = requireUploadResult(
                await this.completeMultipartUpload()
            )
            onProgress?.(1)
            return { audioId, storageKey, url }
        } finally {
            // Clear the callback so it doesn't leak to other flows (singleton).
            this.progressCallback = null
        }
    }

    async abortMultipartUpload(): Promise<void> {
        if (!this.activeUpload) return

        this.status = 'aborting'

        try {
            const command = new AbortMultipartUploadCommand({
                Bucket: this.activeUpload.bucket,
                Key: this.activeUpload.key,
                UploadId: this.activeUpload.uploadId,
            })

            await this.s3Client.send(command)

            logEvent(
                'INFO',
                's3_multipart_aborted',
                'S3 multipart upload aborted',
                { audioId: this.audioId }
            )
        } catch (error) {
            logEvent(
                'ERROR',
                's3_multipart_abort_failed',
                'Failed to abort S3 multipart upload',
                {
                    audioId: this.audioId,
                    error:
                        error instanceof Error ? error.message : String(error),
                }
            )
        } finally {
            // Preserve audioId so the Audio_Blob reference remains available for retry
            const preservedAudioId = this.audioId
            this.cleanUp()
            this.audioId = preservedAudioId
        }
    }

    private cleanUp() {
        this.audioId = null
        this.status = 'idle'
        this.activeUpload = null
        this.uploadQueue = Promise.resolve()
        this.expectedTotalBytes = null
    }

    private async _doUploadChunk(chunk: Blob): Promise<void> {
        if (!this.activeUpload) {
            return
        }

        const partNumber = this.activeUpload.partNumber
        const arrayBuffer = await chunk.arrayBuffer()
        const body = new Uint8Array(arrayBuffer)

        const command = new UploadPartCommand({
            Bucket: this.activeUpload.bucket,
            Key: this.activeUpload.key,
            PartNumber: partNumber,
            UploadId: this.activeUpload.uploadId,
            Body: body,
            ContentLength: chunk.size,
        })

        const maxAttempts = S3_CHUNK_RETRY.maxAttempts

        try {
            const response = await withRetry(
                async () => {
                    await waitForOnline()
                    return this.s3Client.send(command)
                },
                {
                    ...S3_CHUNK_RETRY,
                    shouldRetry: () => true,
                    onRetry: (attempt, delay, error) => {
                        logEvent(
                            'ERROR',
                            's3_chunk_upload_retry',
                            `S3 chunk upload failed, retrying (attempt ${attempt}/${maxAttempts})`,
                            {
                                audioId: this.audioId,
                                partNumber,
                                attemptCount: attempt,
                                maxAttempts,
                                error:
                                    error instanceof Error
                                        ? error.message
                                        : String(error),
                            }
                        )
                    },
                }
            )

            this.activeUpload.addPart(
                {
                    ETag: response.ETag,
                    PartNumber: partNumber,
                },
                chunk.size
            )

            // Emit upload progress
            if (this.progressCallback && this.audioId) {
                let progress: number
                if (this.expectedTotalBytes && this.expectedTotalBytes > 0) {
                    progress = this.activeUpload.size / this.expectedTotalBytes
                } else {
                    // Streaming: estimate based on chunk count
                    const estimatedTotalParts = Math.max(
                        this.activeUpload.partNumber,
                        10
                    )
                    progress =
                        this.activeUpload.parts.length / estimatedTotalParts
                }
                this.progressCallback(this.audioId, Math.min(progress, 0.99))
            }

            logEvent(
                'INFO',
                's3_multipart_chunk_completed',
                'S3 chunk upload succeeded',
                {
                    audioId: this.audioId,
                    partNumber: partNumber,
                    size: this.activeUpload.size,
                }
            )
        } catch (error) {
            logEvent(
                'ERROR',
                's3_chunk_upload_exhausted',
                `S3 chunk upload failed after all ${maxAttempts} retry attempts`,
                {
                    audioId: this.audioId,
                    partNumber,
                    attemptCount: maxAttempts,
                    error:
                        error instanceof Error ? error.message : String(error),
                }
            )
            throw error
        }
    }

    /** Resolves `undefined` when no multipart upload is active. */
    private async completeMultipartUpload(): Promise<UploadResult | undefined> {
        const activeUpload = this.activeUpload
        if (!activeUpload) return

        try {
            activeUpload.sortParts()

            const command = new CompleteMultipartUploadCommand({
                Bucket: activeUpload.bucket,
                Key: activeUpload.key,
                UploadId: activeUpload.uploadId,
                MultipartUpload: {
                    Parts: activeUpload.parts,
                },
            })

            await withRetry(async () => {
                await waitForOnline()
                return this.s3Client.send(command)
            }, S3_MULTIPART_RETRY)

            // Generate presigned URL for the completed upload
            const urlResult = await getUrlResult(activeUpload.key)

            logEvent(
                'INFO',
                's3_multipart_completed',
                'S3 multipart upload completed',
                {
                    audioId: this.audioId,
                    totalParts: activeUpload.parts.length,
                    path: activeUpload.key,
                    size: activeUpload.size,
                }
            )

            this.cleanUp()

            return {
                storageKey: activeUpload.key,
                url: urlResult.url,
            }
        } catch (error) {
            logEvent(
                'ERROR',
                's3_multipart_complete_failed',
                'Failed to complete S3 multipart upload after all retry attempts',
                {
                    audioId: this.audioId,
                    error:
                        error instanceof Error ? error.message : String(error),
                }
            )

            // Only abort after withRetry has exhausted all attempts
            await this.abortMultipartUpload()
            throw error
        }
    }

    private makeStoragePath(
        fileName: string,
        storageType: StorageType
    ): string {
        const basePrefix = Constants.Protected + Constants.Separator
        const user = getTuhiUserId() + Constants.Separator
        const collectionName = Constants.Consults + Constants.Separator
        const type = storageType + Constants.Separator
        const location = Constants.Input + Constants.Separator

        return basePrefix + user + collectionName + type + location + fileName
    }
}

type UploadResult = { storageKey: string; url: URL }

/**
 * Every caller destructures the upload result, so a missing one (no multipart
 * upload was active) used to throw a TypeError there. Throw at the same point
 * with a clear message instead.
 */
function requireUploadResult(result: UploadResult | undefined): UploadResult {
    if (!result) {
        throw new Error('No active S3 multipart upload to complete')
    }
    return result
}

export async function getUrlResult(
    path: string
): Promise<{ url: URL; expiresAt: Date }> {
    return await getUrl({
        path: path,
        options: {
            expiresIn: 3600, // validity of the URL in seconds (max 1 hour)
            useAccelerateEndpoint: true, // whether to use accelerate endpoint
        },
    })
}

export async function mintValidDownloadUrl(key: string): Promise<URL> {
    const urlResult = await getUrlResult(key)
    return urlResult.url
}

export const s3StorageRepository = new S3StorageRepository()
